import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Input } from '@column/column-ui-kit';

import { HintLink, Logo, Panel } from '../_components';
import { NotificationList, PasswordInfo, PasswordStrength, RenderFields } from '~/components';
import { UnderlineLink } from '~/elements';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { ROUTE } from '~/public/routes';
import { DashboardLoginResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Headline } from '~/styles';
import { log, validateEmail } from '~/util';
import { useReCaptcha } from '~/util/reCaptcha';

interface RegistrationFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
}

export const Legal = styled.div`
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const PageRegister: React.FC = () => {
  const getReCaptchaToken = useReCaptcha();
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const { handleFormErrors } = useFormErrorHandler();

  const { signIn, setSession, loadPlatform } = useSessionStore();
  const formMethods = useForm<RegistrationFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
    },
  });
  const { getValues, watch, setValue } = formMethods;
  const [searchParams] = useSearchParams();
  const [isEmailPreconfigured, setIsEmailPreconfigured] = useState<boolean>(false);
  const [passwordInfo, setPasswordInfo] = useState<PasswordInfo>();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('code');

  const createUserMutation = useMutation({
    mutationFn: async () => {
      return new Promise<void>(async (resolve, reject) => {
        const reCaptchaToken = await getReCaptchaToken();
        try {
          await UserRepository.create(
            getValues('firstName'),
            getValues('lastName'),
            getValues('email'),
            getValues('password'),
            inviteCode,
            reCaptchaToken
          );

          log({
            name: 'User registered',
            context: {
              firstName: getValues('firstName'),
              lastName: getValues('lastName'),
              email: getValues('email'),
            },
          });

          resolve();
        } catch (err: any) {
          reject(err);
        }
      });
    },
    onSuccess: () => {
      loginMutation.mutate();
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message,
        display: 'page',
      });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async () => {
      return new Promise<DashboardLoginResponse>(async (resolve, reject) => {
        try {
          const reCaptchaToken = await getReCaptchaToken();
          const response = await UserRepository.login(getValues('email'), getValues('password'), reCaptchaToken);
          resolve(response);
        } catch (err: any) {
          reject(err);
        }
      });
    },
    onSuccess: async (loginResponse: DashboardLoginResponse) => {
      setSession(loginResponse.sessionId, loginResponse.dashboardUserId, { mfaStatus: loginResponse.mfaStatus });
      const signInResp = await signIn();
      if (!signInResp) {
        navigate(ROUTE.LOGIN);
        addDangerNotification({
          content: 'Error signing in, please try again',
          display: 'page',
        });
        return;
      }

      const companyName = getValues('companyName');
      if (companyName) {
        try {
          const loadPlatformResponse = await loadPlatform({ fetchMeta: true });
          await loadPlatformResponse?.updateMeta({ companyName });
        } catch (err: any) {
          addDangerNotification({
            content: err.message,
            display: 'page',
          });
          return;
        }
      }

      if (inviteCode) {
        navigate(ROUTE.SETUP_MFA);
      } else {
        navigate(ROUTE.REGISTER_FINANCIAL_PRODUCTS);
      }
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message,
        display: 'page',
      });
    },
  });

  useEffect(() => {
    const encodedEmail = searchParams.get('email');
    if (encodedEmail) {
      const decodedEmail = decodeURI(encodedEmail);
      setValue('email', decodedEmail);
      setIsEmailPreconfigured(true);
    }
  }, [searchParams, setValue]);

  const onSuccess = () => {
    if (createUserMutation.isPending || loginMutation.isPending) {
      return;
    }

    if (!passwordInfo || passwordInfo.score < 2) {
      addDangerNotification({
        display: 'page',
        content: 'Please choose a stronger password.',
      });
      return;
    }

    createUserMutation.mutate();
  };

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');
  const disallowedStringsForPassword = useMemo(() => {
    return [watch('firstName'), watch('lastName'), watch('email')].filter((s) => s && s.length > 0);
  }, [firstName, lastName, email]);

  const isLoading = createUserMutation.isPending || loginMutation.isPending;

  return (
    <Panel>
      <Logo size="24px" />
      <Flex flexDirection="column">
        <Headline size="small">Create an account</Headline>
      </Flex>
      <FormProvider {...formMethods}>
        <div>
          <NotificationList display="page" spacing="top" />
          <form onSubmit={formMethods.handleSubmit(onSuccess, (errors) => handleFormErrors({ errors }))}>
            {RenderFields({
              sections: [
                {
                  variant: 'isCompact',
                  fields: [
                    {
                      id: 'firstName',
                      noStyle: true,
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                        <Input
                          placeholder="First name"
                          hasError={isTouched && !!error}
                          isDisabled={isLoading}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                        />
                      ),
                    },
                    {
                      id: 'lastName',
                      noStyle: true,
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                        <Input
                          placeholder="Last name"
                          hasError={isTouched && !!error}
                          isDisabled={isLoading}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                        />
                      ),
                    },
                    {
                      id: 'companyName',
                      noStyle: true,
                      hide: !!inviteCode,
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                        <Input
                          placeholder="Company (Optional)"
                          isDisabled={isLoading}
                          hasError={isTouched && !!error}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                        />
                      ),
                    },
                    {
                      id: 'email',
                      noStyle: true,
                      rules: {
                        required: true,
                        validate: validateEmail,
                      },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                        <Input
                          autoComplete="username"
                          placeholder="Email address"
                          hasError={isTouched && !!error}
                          isDisabled={isEmailPreconfigured || isLoading}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                        />
                      ),
                    },
                    {
                      id: 'password',
                      noStyle: true,
                      rules: { required: true },
                      children: ({ value, onChange }, { isTouched, error }) => {
                        const passwordValue = watch('password');
                        return (
                          <PasswordStrength
                            hasError={
                              (isTouched || (typeof passwordValue === 'string' && passwordValue.length > 0)) &&
                              (!!error || (passwordInfo?.score ?? 0) < 2)
                            }
                            value={String(value ?? '')}
                            onChange={onChange}
                            isDisabled={isLoading}
                            disallowedStrings={disallowedStringsForPassword}
                            onStrengthChange={setPasswordInfo}
                          />
                        );
                      },
                    },
                  ],
                },
              ],
            })}
            <Flex flexDirection="column" gap={16} mt={24}>
              <Button isLoading={isLoading}>Sign up</Button>
              <Legal>
                By signing up you agree to our{' '}
                <UnderlineLink href="https://column.com/legal/api-terms-of-service" newTab withoutArrow>
                  API Terms of Service
                </UnderlineLink>{' '}
                and{' '}
                <UnderlineLink href="https://column.com/legal/privacy-policy" newTab withoutArrow>
                  Privacy Policy
                </UnderlineLink>
                .
              </Legal>
              <HintLink as={Link} to={ROUTE.LOGIN}>
                Back to Login
              </HintLink>
            </Flex>
          </form>
        </div>
      </FormProvider>
    </Panel>
  );
};
